import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { formatISTShort } from '../utils/time';

export default function Websites({ kidId, parentId, plan, features, deviceFilter }) {
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [search, setSearch] = useState('');
  const [flaggedOnly, setFlaggedOnly] = useState(false);

  useEffect(() => {
    if (!parentId) { setLoading(false); return; }
    const load = async () => {
      setLoading(true);
      setProgress(20);
      let query = supabase.from('websites').select('*').eq('parent_id', parentId);
      if (kidId) query = query.eq('kid_id', kidId);
      if (deviceFilter && deviceFilter !== 'all') query = query.eq('source', deviceFilter);
      const { data, error } = await query.order('timestamp', { ascending: false }).limit(100);
      if (error) { console.error('Websites fetch error:', error); setLoading(false); return; }
      setProgress(90);
      setWebsites(data || []);
      setProgress(100);
      setLoading(false);
    };
    load();
  }, [parentId, kidId, deviceFilter]);

  // Client-side filtering
  const term = search.trim().toLowerCase();
  const filtered = websites.filter(w => {
    if (flaggedOnly && !w.flagged) return false;
    if (term) {
      const inUrl = (w.url || '').toLowerCase().includes(term);
      const inTitle = (w.title || '').toLowerCase().includes(term);
      if (!inUrl && !inTitle) return false;
    }
    return true;
  });

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">
          Websites <span className="badge badge-blue">{filtered.length}</span>
        </h1>
      </div>

      {/* Search + flagged toggle */}
      <div className="filter-bar">
        <input
          type="text"
          placeholder="Search by URL or title..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 360 }}
        />
        <button
          className={`filter-btn ${flaggedOnly ? 'active' : ''}`}
          onClick={() => setFlaggedOnly(f => !f)}
        >
          Flagged only
        </button>
      </div>

      {loading && (
        <div className="loading-container">
          <div className="loading-icon">{'\uD83C\uDF10'}</div>
          <div className="loading-title">Loading websites...</div>
          <div className="loading-bar-track">
            <div className="loading-bar-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="loading-percent">{progress}%</div>
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="card">
          <div className="empty-state">
            <div className="icon">🌐</div>
            <div className="title">
              {search || flaggedOnly ? 'No matching websites' : 'No websites tracked yet'}
            </div>
            <div className="desc">
              {search || flaggedOnly
                ? 'Try adjusting your search or filters.'
                : 'Make sure the agent is running on your child\'s device.'}
            </div>
          </div>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Site</th>
                <th style={{ width: 100 }}>Status</th>
                <th style={{ width: 140 }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(site => (
                <tr key={site.id} className={site.flagged ? 'flagged' : ''}>
                  <td style={{ maxWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {site.title || site.url}
                    </div>
                    <div style={{ fontSize: 11, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <a
                        href={(site.url || '').startsWith('http') ? site.url : '#'}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: 'var(--muted)', textDecoration: 'none' }}
                      >
                        {site.url}
                      </a>
                    </div>
                  </td>
                  <td>
                    {site.flagged
                      ? <span className="badge badge-red">Flagged</span>
                      : <span className="badge badge-green">Safe</span>}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    {formatISTShort(site.timestamp)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
